import Vue from "vue";
import Router from "vue-router";
import store from "@/store";
import { checkTokenExpiry } from "@/services/auth.service";

const Dashboard = () =>
  import(/* webpackChunkName; "login" */ "@/views/Dashboard.vue");
const Unauthorised = () =>
  import(/* webpackChunkName; "auth" */ "@/views/Unauthorised.vue");
const UserCreate = () =>
  import(/* webpackChunkName: "auth" */ "@/views/UserCreate.vue");
const UserChange = () =>
  import(/* webpackChunkName: "auth" */ "@/views/UserChange.vue");
const UserIndex = () =>
  import(/* webpackChunkName: "auth" */ "@/views/UserIndex.vue");
const UserActivate = () =>
  import(/* webpackChunkName: "auth" */ "@/views/UserActivate.vue");
const ResetPassword = () =>
  import(/* webpackChunkName: "auth" */ "@/views/PasswordReset.vue");
const ResetPasswordConfirm = () =>
  import(/* webpackChunkName: "auth" */ "@/views/PasswordResetConfirm.vue");
// Records module
const RecordIndex = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/RecordIndex.vue");
const RecordCreate = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/RecordCreate.vue");
const RecordDetail = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/RecordDetail.vue");
const RecordUserFileIndex = () =>
  import(
    /* webpackChunkName; "records" */ "@/views/records/RecordUserFileIndex"
  );
const RecordUserFile = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/RecordUserFile");
const ScannedRecordIndex = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/ScannedRecordIndex");
const ScannedRecordCreate = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/ScannedRecordCreate");
const ScannedRecordUpdate = () =>
  import(/* webpackChunkName; "records" */ "@/views/records/ScannedRecordUpdate");
// Exams module
const ExamIndex = () =>
  import(/* webpackChunkName; "exams" */ "@/views/ExamIndex.vue");
const ExamDetail = () =>
  import(/* webpackChunkName; "exams" */ "@/views/ExamDetail.vue");
const ExamCreate = () =>
  import(/* webpackChunkName; "exams" */ "@/views/ExamCreate.vue");
const ExamKDR = () =>
  import(/* webpackChunkName; "exams" */ "@/views/ExamKDR.vue");
const QuestionIndex = () =>
  import(/* webpackChunkName; "questions" */ "@/views/QuestionIndex.vue");
const QuestionEdit = () =>
  import(/* webpackChunkName; "questions" */ "@/views/QuestionEdit.vue");
const QuestionCreate = () =>
  import(/* webpackChunkName; "questions" */ "@/views/QuestionCreate.vue");

// Courses module
const CourseIndex = () =>
  import(/* webpackChunkName; "questions" */ "@/views/courses/CourseIndex.vue");
const CourseDetail = () =>
  import(/* webpackChunkName; "questions" */ "@/views/courses/CourseDetail.vue");
const CourseCreate = () =>
  import(/* webpackChunkName; "questions" */ "@/views/courses/CourseCreate.vue");


Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    // Root and generic views
    {
      path: "/",
      name: "dashboard",
      component: Dashboard
    },
    {
      path: "/unauthorised",
      name: "unauthorised",
      component: Unauthorised
    },
    // Auth module (users etc.)
    {
      path: "/users/create",
      name: "user-create",
      component: UserCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("auth-taslytic.add_taslyticuser");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/users/:userId",
      name: "user-change",
      component: UserChange
    },
    {
      path: "/users/activate/:uid/:token",
      name: "user-activate",
      component: UserActivate
    },
    {
      path: "/users/reset_password/confirm/:uid/:token",
      name: "reset-password-confirm",
      component: ResetPasswordConfirm
    },
    {
      path: "/users/reset_password",
      name: "reset-password",
      component: ResetPassword
    },
    {
      path: "/users",
      name: "user-index",
      component: UserIndex,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("auth-taslytic.view_taslyticuser");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    // Records module
    {
      path: "/records",
      name: "record-index",
      component: RecordIndex
    },
    {
      path: "/records/create",
      name: "record-create",
      component: RecordCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.add_record");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/records/:id(\\d+)",
      name: "record-detail",
      component: RecordDetail
    },
    {
      path: "/records/files",
      name: "record-file-index",
      component: RecordUserFileIndex,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.view_record");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/records/files/:id(\\d+)",
      name: "record-file",
      component: RecordUserFile,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.view_record");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/records/scanned",
      name: "scanned-record-index",
      component: ScannedRecordIndex,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.view_scannedrecord");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/records/scanned/create",
      name: "scanned-record-create",
      component: ScannedRecordCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.add_scannedrecord");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/records/scanned/:id/",
      name: "scanned-record-update",
      component: ScannedRecordUpdate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("records.change_scannedrecord");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    // Courses module
    {
      path: "/courses",
      name: "course-index",
      component: CourseIndex
    },
    {
      path: "/courses/:id",
      name: "course-detail",
      component: CourseDetail
    },
    {
      path: "/courses/create",
      name: "course-create",
      component: CourseCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("courses.add_course");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },


    // Exams module
    {
      path: "/exams/create",
      name: "exam-create",
      component: ExamCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("exams.add_exam");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/exams",
      name: "exam-index",
      component: ExamIndex
    },
    {
      path: "/exams/:id",
      name: "exam-detail",
      component: ExamDetail
    },
    {
      path: "/exams/:id/kdr",
      name: "exam-kdr",
      component: ExamKDR
    },
    {
      path: "/questions/create",
      name: "question-create",
      component: QuestionCreate,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("exams.add_question");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/questions",
      name: "question-index",
      component: QuestionIndex,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("exams.view_question");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/questions/:id",
      name: "question",
      component: QuestionEdit,
      beforeEnter: (to, from, next) => {
        let permissions = store.getters["auth/permissions"];
        let has_perm = permissions.includes("exams.view_question");
        if (has_perm) {
          next();
        } else {
          next({ name: "unauthorised" });
        }
      }
    },
    {
      path: "/login",
      name: "login",
      component: () =>
        import(/* webpackChunkName: "login" */ "@/views/Login.vue")
    },
    {
      path: "/change-password",
      name: "change-password",
      component: () =>
        import(/* webpackChunkName: "login" */ "@/views/PasswordChange.vue")
    },
    {
      path: "/change-signature",
      name: "change-signature",
      component: () =>
        import(/* webpackChunkName: "login" */ "@/views/SignatureChange.vue")
    }
  ]
});

var nextUrl = null;

// Any named routes in `safeRoutes` are bypass the beforeEach route guard
const safeRoutes = [
  "login",
  "user-activate",
  "reset-password",
  "reset-password-confirm"
];

// Setup the navigation guard to only allow logged in users to access the site
router.beforeEach((to, from, next) => {
  const tokens = store.getters["auth/tokens"];
  // Note that this route guard just makes sure that an access token exists and
  // that it's timestamp is valid.
  //
  // Any actual authorisation errors need to be handled by the view and/or
  // component.
  if (tokens.access && checkTokenExpiry(tokens.access)) {
    if (to.name == "login") {
      // Don't go back to the login screen if already logged in
      next({ name: "dashboard" });
    } else if (nextUrl) {
      let path = nextUrl;
      nextUrl = null;
      next(path);
    } else {
      next();
    }
  } else {
    // Handle cases where the token is refreshed or the user logs in again
    if (safeRoutes.includes(to.name)) {
      // Let the route proceed to the login/activate/reset page (etc. etc.) if
      // we're already going there!
      next();
    } else {
      // The authenticated state is persisted by storing the refresh token in a
      // httpOnly cookie. We know whether this exists *and should be used* by
      // the `has_refresh_token` var in localStorage.
      //
      // (This var is set on login and cleared on logout.)
      //
      // If a refresh cookie exists *AND* if we should try to use it, then try
      // to refresh the access token and then keep on going. Otherwise keep
      // track of where we're trying to get to and navigate back to the login
      // page to get new tokens.

      /* eslint-disable no-unused-vars */
      if (localStorage.getItem("has_refresh_cookie") == "True") {
        store
          .dispatch("auth/refreshToken")
          .then(response => {
            if (response.status == 200) {
              store.commit("auth/setAccessToken", response.data.access);
              next();
            } else {
              store.dispatch("auth/logout");
              nextUrl = to.fullPath;
              next(
                { name: "login" },
                onComplete => {},
                onAbort => {}
              );
            }
          })
          .catch(() => {
            // Refresh failed so we need to log in again
            store.dispatch("auth/logout");
            nextUrl = to.fullPath;
            next(
              { name: "login" },
              onComplete => {},
              onAbort => {}
            );
          });
      } else {
        // We don't try to refresh so we need to log in again
        store.dispatch("auth/logout");
        nextUrl = to.fullPath;
        next(
          { name: "login" },
          onComplete => {},
          onAbort => {}
        );
      }
    }
  }
});

export default router;
